import { useEffect, useRef, useState } from 'react';
import { useApolloClient, useQuery } from '@apollo/client';
import {
  GET_TT_LOCATION_CONFIGS_WITH_PAGINATION,
  GET_TT_LOCATION_CONFIG_CHILDREN,
  TtLocationConfigPageResponse,
  TtLocationConfigChildrenResponse,
  TtLocationConfigItem,
} from '../graphql/queries/getTtLocationConfigTree';
import { useFinalMileCarrierOptions } from '../pages/wms/sorterPlan/hooks/useFinalMileCarrierOptions';

/** 尾程商下的「服务」子节点（code=分拣规则第三段码，labelCode 仅展示不参与匹配） */
export type CarrierServiceItem = TtLocationConfigItem;

/** tt_location_config 承运商顶级节点的 location_type */
const CARRIER_LOCATION_TYPE = 5;

/**
 * 构建「尾程商 carrierCode(FinalMileCarrier.value) → 服务列表」映射。
 *
 * 步骤：
 * 1. 拉取全部 location_type=5 承运商节点，得到「承运商 code(=枚举 name) → 节点 id」；
 * 2. 用 useFinalMileCarrierOptions 得到「carrierCode(value) → 枚举 name」；
 * 3. 对每个承运商节点预取 children（服务），归并成 carrierCode(value) → 服务列表。
 *
 * @param enabled 建包表单常驻挂载，用可见性驱动重新拉取：由「关闭→打开」时清空已取记录 + 走网络，
 *   保证每次打开都反映期间在配置管理页新增/修改/删除的服务，而不必整页刷新。
 */
export const useCarrierServiceMap = (enabled: boolean = true) => {
  const client = useApolloClient();
  const { options: carrierOptions } = useFinalMileCarrierOptions(true);
  const { data: carrierNodesData, loading: nodesLoading } = useQuery<TtLocationConfigPageResponse>(
    GET_TT_LOCATION_CONFIGS_WITH_PAGINATION,
    {
      variables: { filter: { locationType: CARRIER_LOCATION_TYPE }, pageRequest: { page: 1, size: 200 } },
      fetchPolicy: 'cache-and-network',
      skip: !enabled,
    }
  );

  const [serviceMap, setServiceMap] = useState<Map<number, CarrierServiceItem[]>>(new Map());
  const [childrenLoading, setChildrenLoading] = useState(false);
  // 已发起 children 查询的承运商节点 id，避免 effect 重跑重复请求
  const fetchedNodeIdsRef = useRef<Set<number>>(new Set());

  // 「关闭→打开」时重置已取记录，使每次打开都重新按最新配置拉取服务子节点
  useEffect(() => {
    if (enabled) fetchedNodeIdsRef.current.clear();
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return;
    const carrierNodes = carrierNodesData?.getTtLocationConfigsWithPagination.content ?? [];
    if (carrierNodes.length === 0 || carrierOptions.length === 0) return;

    // 枚举 name → carrierCode(value)
    const nameToValue = new Map<string, number>();
    carrierOptions.forEach(o => nameToValue.set(o.name, o.value));

    const pending = carrierNodes.filter(node => !fetchedNodeIdsRef.current.has(node.id));
    if (pending.length === 0) return;

    setChildrenLoading(true);
    Promise.all(
      pending.map(async node => {
        fetchedNodeIdsRef.current.add(node.id);
        const carrierValue = nameToValue.get(node.code);
        if (carrierValue == null) return;
        try {
          // network-only：始终拉最新服务子节点（配合打开时清 ref，反映新增/修改/删除）
          const { data } = await client.query<TtLocationConfigChildrenResponse>({
            query: GET_TT_LOCATION_CONFIG_CHILDREN,
            variables: { parentId: node.id },
            fetchPolicy: 'network-only',
          });
          const children = data?.getTtLocationConfigChildren ?? [];
          setServiceMap(prev => {
            const next = new Map(prev);
            if (children.length > 0) next.set(carrierValue, children);
            else next.delete(carrierValue); // 服务被全部删除时同步清掉，避免展示已删项
            return next;
          });
        } catch (e) {
          // 查询失败时允许下次重试
          fetchedNodeIdsRef.current.delete(node.id);
        }
      })
    ).finally(() => setChildrenLoading(false));
  }, [enabled, carrierNodesData, carrierOptions, client]);

  return { serviceMap, loading: nodesLoading || childrenLoading };
};
